import type { Task } from "~skillBuilder/utils/types";

import { image, tip, title } from "../../contentBlocks";

export const PReflection = {
  name: "Reflection",
  uniqueName: "p-reflection",
  icon: "Lesson",
  durationTimeText: "10 min",
  steps: [
    // Step 1
    {
      nextButtonText: "Start",
      questions: [
        // Question 1.1
        {
          attribute: "professionalism.reflection/overview",
          label: "",
          answerType: {
            type: "CONTENT",
            value: "valid",
            markdown: `
${title({ text: "Reflection", durationTimeText: "10 min" })}
<p>Taking time to reflect after each request fosters self-awareness, personal accountability, and continuous growth. By learning from both successes and setbacks, you sharpen your decision-making and strengthen your skills.</p>

<p>With a mindset of continuous improvement, you become more adaptable and consistently maintain high standards of professionalism in every classroom.</p>
<div align="center">
<div style="max-width:250px;width:100%;">
${image({ url: "https://res.cloudinary.com/swingeducation/image/upload/fl_preserve_transparency/v1758202463/skillbuilderAssets/optimized/reflection.jpg", alt: "Women holding a paper and reflecting." })}
</div>
</div>
            `,
          },
        },
      ],
    },
    // Step 2
    {
      nextButtonText: "Next",
      questions: [
        // Question 2.1
        {
          attribute: "professionalism.reflection/connection",
          label: "Reflect on your communication and connection",
          answerType: {
            type: "CONTENT",
            value: "valid",
            markdown: `
<ul style="margin-left: 16px;">
<li>🗣️ <strong>Staff & Students:</strong> How well did I communicate with staff, students, and support personnel?</li>
<li>👔 <strong>Professionalism:</strong> Did I present myself professionally in both appearance and demeanor?</li>
<li>🛠️ <strong>Handling Challenges:</strong> Did I handle unexpected situations calmly and effectively?</li>
</ul>

Be sure to assess the clarity and tone of your instructions and feedback. Identify if there are any opportunities to improve your active listening or non-verbal communication skills.
            `,
          },
        },
      ],
    },
    // Step 3
    {
      nextButtonText: "Next",
      questions: [
        // Question 3.1
        {
          attribute: "professionalism.reflection/students",
          label: "Reflect on your interactions with the students",
          answerType: {
            type: "CONTENT",
            value: "valid",
            markdown: `
Ask yourself:

<ul style="margin-left: 16px;">
<li>🧑‍🏫 <strong>Managing the Classroom:</strong> Did I effectively maintain a positive learning environment?</li>
<li>📚 <strong>Lesson Engagement:</strong> How well did I engage students with the provided lesson plan?</li>
<li>⚠️ <strong>Behavior Challenges:</strong> Were there any behavioral issues? How did I handle them, and what might I do differently next time?</li>
<li>😌 <strong>Emotional Awareness:</strong> Did I show any frustration or annoyance throughout the day?</li>
<li>🛠️ <strong>Stress Management:</strong> How did I handle stress or unexpected challenges in the classroom?</li>
</ul>

Reflect on how well you established trust and maintained a mutually respectful classroom environment. Identify strategies to stay calm, focused, and effective during challenging situations.
            `,
          },
        },
      ],
    },
    // Step 4
    {
      nextButtonText: "Next",
      questions: [
        // Question 4.1
        {
          attribute: "professionalism.reflection/growth",
          label: "Reflect on your growth",
          answerType: {
            type: "CONTENT",
            value: "valid",
            markdown: `
Ask yourself:

<ul style="margin-left: 16px;">
<li>🏆 <strong>Successes:</strong> What was my biggest success today?</li>
<li>😊 <strong>Positive Impact:</strong> How did I enhance the student learning experience?</li>
<li>🔄 <strong>Opportunities:</strong> What could I have done differently today?</li>
<li>💡 <strong>Learning Moments:</strong> Were there any situations I could grow from?</li>
</ul>

Pat yourself on the back for the things you did well and identify any growth areas you'd like to work on next time.
            `,
          },
        },
      ],
    },
    // Step 5
    {
      nextButtonText: "Next",
      questions: [
        // Question 5.1
        {
          attribute: "professionalism.reflection/preferences",
          label: "Align your preferences",
          answerType: {
            type: "CONTENT",
            value: "valid",
            markdown: `
As you gain experience in different schools and classrooms, focus on accepting requests that align with your interests and strengths.

This will allow you to contribute positively to each school environment while doing what you enjoy most.

${tip({ text: "Be sure to read the request details and notes thoroughly before accepting to ensure you feel comfortable with the support that the school is needing." })}
            `,
          },
        },
      ],
    },
    // Step 6
    {
      nextButtonText: "Next Lesson",
      questions: [
        // Question 6.1
        {
          attribute: "professionalism.reflection/confident",
          label: "Be confident",
          answerType: {
            type: "CONTENT",
            value: "valid",
            markdown: `
Regular reflection builds confidence in your practice. Even on challenging days, it helps you recognize your strengths, develop positive solutions, and prepare for the future.

By identifying what worked and what didn't, you gain valuable insights that give you the confidence to make the next day even better! 🌟
            `,
          },
        },
      ],
    },
  ],
} as const satisfies Task;
